/**
 * Funções de validação de entrada para diferentes tipos de dados
 * Usadas no flow builder para validar respostas dos usuários
 */

/**
 * Interface de resultado da validação
 */
export interface ValidationResult {
  isValid: boolean;
  error?: string;
}

/**
 * Valida formato de e-mail
 */
export function validateEmail(value: string): ValidationResult {
  const trimmed = value.trim();
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  
  if (!emailRegex.test(trimmed)) {
    return {
      isValid: false,
      error: 'Por favor, insira um e-mail válido (ex: usuario@exemplo.com)',
    };
  }
  
  return { isValid: true };
}

/**
 * Valida formato de telefone brasileiro
 * Aceita: (11) 99999-9999, 11999999999, +5511999999999
 */
export function validatePhone(value: string): ValidationResult {
  // Remove tudo que não é dígito
  const digits = value.replace(/\D/g, '');
  
  // Aceita 10 dígitos (fixo) ou 11 dígitos (celular)
  // Pode começar com 55 (código do Brasil)
  const phoneRegex = /^(55)?(\d{2})(\d{8,9})$/;
  
  if (!phoneRegex.test(digits)) {
    return {
      isValid: false,
      error: 'Por favor, insira um telefone válido com DDD (ex: 11999999999 ou (11) 99999-9999)',
    };
  }
  
  return { isValid: true };
}

/**
 * Valida CPF brasileiro
 */
export function validateCPF(value: string): ValidationResult {
  // Remove tudo que não é dígito
  const cpf = value.replace(/\D/g, '');
  
  // Verifica se tem 11 dígitos
  if (cpf.length !== 11) {
    return {
      isValid: false,
      error: 'CPF deve conter 11 dígitos (ex: 123.456.789-00)',
    };
  }
  
  // Verifica se todos os dígitos são iguais (CPF inválido)
  if (/^(\d)\1{10}$/.test(cpf)) {
    return {
      isValid: false,
      error: 'CPF inválido',
    };
  }
  
  // Validação dos dígitos verificadores
  let sum = 0;
  let remainder: number;
  
  // Primeiro dígito verificador
  for (let i = 1; i <= 9; i++) {
    sum += parseInt(cpf.substring(i - 1, i)) * (11 - i);
  }
  remainder = (sum * 10) % 11;
  if (remainder === 10 || remainder === 11) remainder = 0;
  if (remainder !== parseInt(cpf.substring(9, 10))) {
    return {
      isValid: false,
      error: 'CPF inválido',
    };
  }
  
  // Segundo dígito verificador
  sum = 0;
  for (let i = 1; i <= 10; i++) {
    sum += parseInt(cpf.substring(i - 1, i)) * (12 - i);
  }
  remainder = (sum * 10) % 11;
  if (remainder === 10 || remainder === 11) remainder = 0;
  if (remainder !== parseInt(cpf.substring(10, 11))) {
    return {
      isValid: false,
      error: 'CPF inválido',
    };
  }
  
  return { isValid: true };
}

/**
 * Valida CNPJ brasileiro
 */
export function validateCNPJ(value: string): ValidationResult {
  // Remove tudo que não é dígito
  const cnpj = value.replace(/\D/g, '');
  
  // Verifica se tem 14 dígitos
  if (cnpj.length !== 14) {
    return {
      isValid: false,
      error: 'CNPJ deve conter 14 dígitos (ex: 12.345.678/0001-90)',
    };
  }
  
  // Verifica se todos os dígitos são iguais (CNPJ inválido)
  if (/^(\d)\1{13}$/.test(cnpj)) {
    return {
      isValid: false,
      error: 'CNPJ inválido',
    };
  }
  
  // Validação do primeiro dígito verificador
  let length = cnpj.length - 2;
  let numbers = cnpj.substring(0, length);
  const digits = cnpj.substring(length);
  let sum = 0;
  let pos = length - 7;
  
  for (let i = length; i >= 1; i--) {
    sum += parseInt(numbers.charAt(length - i)) * pos--;
    if (pos < 2) pos = 9;
  }
  
  let result = sum % 11 < 2 ? 0 : 11 - (sum % 11);
  if (result !== parseInt(digits.charAt(0))) {
    return {
      isValid: false,
      error: 'CNPJ inválido',
    };
  }
  
  // Validação do segundo dígito verificador
  length = length + 1;
  numbers = cnpj.substring(0, length);
  sum = 0;
  pos = length - 7;
  
  for (let i = length; i >= 1; i--) {
    sum += parseInt(numbers.charAt(length - i)) * pos--;
    if (pos < 2) pos = 9;
  }
  
  result = sum % 11 < 2 ? 0 : 11 - (sum % 11);
  if (result !== parseInt(digits.charAt(1))) {
    return {
      isValid: false,
      error: 'CNPJ inválido',
    };
  }
  
  return { isValid: true };
}

/**
 * Valida se é um número válido
 */
export function validateNumber(value: string): ValidationResult {
  const trimmed = value.trim();
  const number = Number(trimmed);
  
  if (isNaN(number)) {
    return {
      isValid: false,
      error: 'Por favor, insira um número válido',
    };
  }
  
  return { isValid: true };
}

/**
 * Valida formato de data (DD/MM/YYYY ou YYYY-MM-DD)
 */
export function validateDate(value: string): ValidationResult {
  const trimmed = value.trim();
  
  // Aceita DD/MM/YYYY ou YYYY-MM-DD
  const dateRegexBR = /^(\d{2})\/(\d{2})\/(\d{4})$/;
  const dateRegexISO = /^(\d{4})-(\d{2})-(\d{2})$/;
  
  let day: number, month: number, year: number;
  
  if (dateRegexBR.test(trimmed)) {
    const match = trimmed.match(dateRegexBR)!;
    day = parseInt(match[1]);
    month = parseInt(match[2]);
    year = parseInt(match[3]);
  } else if (dateRegexISO.test(trimmed)) {
    const match = trimmed.match(dateRegexISO)!;
    year = parseInt(match[1]);
    month = parseInt(match[2]);
    day = parseInt(match[3]);
  } else {
    return {
      isValid: false,
      error: 'Por favor, insira uma data válida (ex: 25/12/2024 ou 2024-12-25)',
    };
  }
  
  // Verifica se a data é válida
  if (month < 1 || month > 12) {
    return { isValid: false, error: 'Mês inválido' };
  }
  
  const daysInMonth = new Date(year, month, 0).getDate();
  if (day < 1 || day > daysInMonth) {
    return { isValid: false, error: 'Dia inválido para o mês informado' };
  }
  
  return { isValid: true };
}

/**
 * Valida CEP brasileiro
 * Formato: 8 dígitos (com ou sem hífen)
 */
export function validateCEP(value: string): ValidationResult {
  // Remove tudo que não é dígito
  const cep = value.replace(/\D/g, '');
  
  // Verifica se tem 8 dígitos
  if (cep.length !== 8) {
    return {
      isValid: false,
      error: 'CEP deve conter 8 dígitos (ex: 01234-567)',
    };
  }
  
  return { isValid: true };
}

/**
 * Valida senha forte (mínimo 8 caracteres, máximo 72, com pelo menos 1 letra e 1 número)
 * Limite de 72 vem do bcrypt (Supabase Auth).
 */
export function validatePassword(value: string): ValidationResult {
  if (!value || value.length < 8) {
    return { isValid: false, error: 'A senha deve ter pelo menos 8 caracteres' };
  }
  if (value.length > 72) {
    return { isValid: false, error: 'A senha deve ter no máximo 72 caracteres' };
  }
  if (!/[A-Za-zÀ-ÿ]/.test(value) || !/\d/.test(value)) {
    return {
      isValid: false,
      error: 'A senha deve conter pelo menos 1 letra e 1 número',
    };
  }
  return { isValid: true };
}

/**
 * Valida nome completo
 * - 3 a 120 caracteres
 * - Aceita apenas letras (incluindo acentuadas), espaços, hífen, ponto e apóstrofo
 * - Bloqueia explicitamente caracteres usados em injeção (<, >, {, }, /, \, etc.)
 */
export function validateFullName(value: string): ValidationResult {
  const trimmed = (value || '').trim();
  if (trimmed.length < 3) {
    return { isValid: false, error: 'Informe seu nome completo (mínimo 3 caracteres)' };
  }
  if (trimmed.length > 120) {
    return { isValid: false, error: 'Nome muito longo (máximo 120 caracteres)' };
  }
  // Letras unicode + espaço + hífen + ponto + apóstrofo
  const nameRegex = /^[\p{L}\s'.\-]+$/u;
  if (!nameRegex.test(trimmed)) {
    return {
      isValid: false,
      error: 'O nome contém caracteres inválidos. Use apenas letras, espaços e hífen.',
    };
  }
  return { isValid: true };
}

/**
 * Função principal que valida baseado no tipo de dado
 */
export function validateInput(
  value: string,
  dataType: 'text' | 'number' | 'email' | 'phone' | 'cpf' | 'cnpj' | 'date',
  customMessage?: string
): ValidationResult {
  // Texto sempre é válido (sem validação específica)
  if (dataType === 'text') {
    return { isValid: true };
  }
  
  let result: ValidationResult;
  
  switch (dataType) {
    case 'email':
      result = validateEmail(value);
      break;
    case 'phone':
      result = validatePhone(value);
      break;
    case 'cpf':
      result = validateCPF(value);
      break;
    case 'cnpj':
      result = validateCNPJ(value);
      break;
    case 'number':
      result = validateNumber(value);
      break;
    case 'date':
      result = validateDate(value);
      break;
    default:
      result = { isValid: true };
  }
  
  // Se tem mensagem customizada, substitui a mensagem padrão
  if (!result.isValid && customMessage) {
    result.error = customMessage;
  }
  
  return result;
}
